import React, { memo, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { format } from 'date-fns';

import { useMutedCallback } from 'hooks/useMutedCallback';

import { PromotionDetails } from '../../types';
import { PromotionTable } from '../PromotionTable/PromotionTable';

export interface AssignedPromotionsListFCProps {
  strings: {
    page: {
      title: string;
      subtitle: string;
      subtitleNoOrders: string;
    };
    activePromotionTableName: string;
    endedPromotionTableName: string;
    buttons: {
      newPromotionButton: string;
    };
    messages: {
      noAssignedPromotions: string;
      errorFetchingAssignedPromotions: string;
      pagerShowRowsFrom: string;
    };
  };

  activePromotionDetails: PromotionDetails[];
  endedPromotionDetails: PromotionDetails[];
  lastUpdate: Date;
  isUpdatedByOrder: boolean;

  promotionClicked: (id: number) => void;
  onClickNewPromotion: () => void;
  rowsPerEndedPromotions?: number;
  canSelfAssignCampaigns: boolean;
}

export const AssignedPromotionsListFC: React.FC<AssignedPromotionsListFCProps> = memo(
  ({
    strings,
    activePromotionDetails,
    endedPromotionDetails,
    lastUpdate,
    isUpdatedByOrder,
    promotionClicked,
    onClickNewPromotion,
    canSelfAssignCampaigns,
    rowsPerEndedPromotions,
  }) => {
    const onClickNewPromotionHandler = useMutedCallback(onClickNewPromotion);

    const subtitle = useMemo(() => {
      if (isUpdatedByOrder) {
        const lastUpdateString = format(lastUpdate, 'MMM dd, yyyy, HH:mm');
        return `${strings.page.subtitle} ${lastUpdateString}`;
      }

      return strings.page.subtitleNoOrders;
    }, [isUpdatedByOrder, lastUpdate, strings]);

    return (
      <Box>
        <Box>
          <Box display="flex" justifyContent="space-between">
            <Box alignSelf="left" justifyContent="space-between">
              <Typography variant="h3">{strings.page.title}</Typography>
              <Typography variant="caption">{subtitle}</Typography>
            </Box>
            <Box alignSelf="right" whiteSpace="nowrap">
              {canSelfAssignCampaigns && (
                <Button onClick={onClickNewPromotionHandler} variant="contained" color="primary">
                  {strings.buttons.newPromotionButton}
                </Button>
              )}
            </Box>
          </Box>
          {activePromotionDetails.length > 0 && (
            <PromotionTable
              promotionDetailList={activePromotionDetails}
              promotionClicked={promotionClicked}
              tableName={strings.activePromotionTableName}
            />
          )}
          {endedPromotionDetails.length > 0 && (
            <PromotionTable
              promotionDetailList={endedPromotionDetails}
              promotionClicked={promotionClicked}
              tableName={strings.endedPromotionTableName}
              showPagination
              rowsPerPage={rowsPerEndedPromotions}
            />
          )}
        </Box>
      </Box>
    );
  },
);
